import {useApp} from "../app.context";
import {useIntegrator} from "../integrator.context";
import {AccessLevel} from "../models/accessLevel.model";

// The canonical "main" branch. Persisting to it (and publishing to live) is
// Admin+; everyone else edits it only as a local sandbox. Kept here as the one
// place the name lives on the frontend — the backend enforces the same name in
// SetRemoteConstants.
export const MAIN_BRANCH = 'Master';
export const isMainBranch = (branch: string) => branch === MAIN_BRANCH;

// Central role→capability map. Every role gate in the UI reads from here instead
// of comparing AccessLevel inline, so the policy lives in exactly one place and
// higher roles inherit lower ones (all checks are `>=`). Mirrors the backend
// enforcement in Secret.cs / SetRemoteConstants.cs / SetUserRole.cs — the UI
// only hides what the server already refuses.
//
// Role summary:
//   Viewer   — fully read-only
//   Guest    — local sandbox: edit in-memory, cannot persist anything
//   Tester   — + export to dev/test (non-Master), uploads, workshop, preview, avatar catalogue
//   Admin    — + export to staging/live, publish avatars live, edit Master, remove branches, see Users
//   SysAdmin — + edit other users' roles
export function useAccess() {
    const {accessLevel} = useApp();
    const {branch} = useIntegrator();

    const atLeast = (level: AccessLevel) => accessLevel >= level;

    // In-memory data edits (executeCommand, undo/redo). Guest+ (sandbox).
    const canEditData = atLeast(AccessLevel.Guest);
    // Read-only means "cannot even edit locally" — Viewer (and None).
    const isReadOnly = !canEditData;

    // Server-touching content writes: asset upload / remove.bg, workshop save.
    const canServerWrite = atLeast(AccessLevel.Tester);
    // Live Theme Preview broadcast.
    const canPreview = atLeast(AccessLevel.Tester);
    // Branch lifecycle.
    const canCreateBranch = atLeast(AccessLevel.Tester);
    const canDeleteBranch = atLeast(AccessLevel.Admin);
    // Persisting / publishing.
    const canEditMaster = atLeast(AccessLevel.Admin);
    const canExportLive = atLeast(AccessLevel.Admin);
    // Dev/test export: Tester+, but Master is Admin-only to persist.
    const canExportDevOn = (b: string) => atLeast(AccessLevel.Tester) && (!isMainBranch(b) || canEditMaster);
    const canExportDev = canExportDevOn(branch);
    // The shared "default" workshop is Admin-only to overwrite/delete.
    const canSaveDefaultWorkshop = atLeast(AccessLevel.Admin);
    // Authored maps: publishing is a server write (Tester+); deleting
    // (manifest tombstone) is Admin+. Local map drafting is Guest+ like
    // other in-memory edits.
    const canPublishMap = atLeast(AccessLevel.Tester);
    const canDeleteMap = atLeast(AccessLevel.Admin);
    // Promoting to (and withdrawing from) live is what players actually see, so
    // Admin+ — the same bar as exporting a branch live.
    const canPromoteMap = atLeast(AccessLevel.Admin);
    // Users tab (users/games list + env toggle) is Admin+; mutating roles is
    // SysAdmin-only.
    const canManageUsers = atLeast(AccessLevel.Admin);
    const canEditRoles = atLeast(AccessLevel.SysAdmin);
    // Avatar tab: the global avatar element catalogue is not branch data —
    // every save is immediate to the shared DEV catalogue, which is Tester
    // territory like other dev-scope server writes. Publishing the catalogue
    // to live is what players see, so Admin+ — the same split as
    // canExportDev / canExportLive.
    const canEditAvatars = atLeast(AccessLevel.Tester);
    const canPublishAvatarsLive = atLeast(AccessLevel.Admin);

    return {
        accessLevel,
        atLeast,
        isMainBranch,
        isReadOnly,
        canEditData,
        canServerWrite,
        canPreview,
        canCreateBranch,
        canDeleteBranch,
        canEditMaster,
        canExportLive,
        canExportDev,
        canExportDevOn,
        canSaveDefaultWorkshop,
        canPublishMap,
        canDeleteMap,
        canPromoteMap,
        canManageUsers,
        canEditRoles,
        canEditAvatars,
        canPublishAvatarsLive,
    };
}
